import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { NotificationItem } from "@/components/NotificationItem";

export default function NotificationsBell() {
  const { user } = useAuth();
  if (!user?.id) return null;
  return <AccountNotifications key={user.id} />;
}

function AccountNotifications() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loadState, setLoadState] = useState("loading");
  const [actionError, setActionError] = useState("");
  const [busy, setBusy] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const mounted = useRef(false);
  const loadVersion = useRef(0);
  const mutating = useRef(false);
  const morePending = useRef(false);
  const browsingOlder = useRef(false);
  const refreshPending = useRef(false);
  const panelOpen = useRef(false);

  const load = useCallback(async () => {
    if (!mounted.current || mutating.current) return;
    const version = ++loadVersion.current;
    const current = () => mounted.current && version === loadVersion.current;
    refreshPending.current = true;
    browsingOlder.current = false;
    morePending.current = false;
    setMoreBusy(false);
    setMoreError(false);
    setLoadState("loading");
    try {
      const r = await api.get("/notifications", { timeout: 30000 });
      if (!current()) return;
      setItems(r.data.items);
      setUnread(r.data.unread);
      setNextCursor(r.data.next_cursor || null);
      setLoadState("ready");
    } catch (err) {
      if (current()) setLoadState("error");
    } finally {
      if (current()) refreshPending.current = false;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    // Keep the history being read stable until the member closes or refreshes.
    const t = setInterval(() => {
      if (!browsingOlder.current) load();
    }, 30000);
    return () => {
      mounted.current = false;
      clearInterval(t);
    };
  }, [load]);

  const closePanel = useCallback(() => {
    panelOpen.current = false;
    setOpen(false);
    if (browsingOlder.current) load();
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closePanel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, closePanel]);

  const loadMore = async () => {
    if (!mounted.current || !panelOpen.current || mutating.current || morePending.current
      || refreshPending.current || loadState !== "ready" || !nextCursor) return;
    const cursor = nextCursor;
    const version = loadVersion.current;
    const current = () => mounted.current && version === loadVersion.current;
    morePending.current = true;
    browsingOlder.current = true;
    setMoreBusy(true);
    setMoreError(false);
    try {
      const r = await api.get("/notifications", { params: { cursor }, timeout: 30000 });
      if (!current()) return;
      if (!Array.isArray(r.data.items) || r.data.next_cursor === cursor || (r.data.next_cursor != null
        && (typeof r.data.next_cursor !== "string" || !r.data.next_cursor))) {
        throw new Error("Invalid history page");
      }
      setItems((previous) => [...new Map([...previous, ...r.data.items].map((item) => [item.id, item])).values()]);
      setUnread(r.data.unread);
      setNextCursor(r.data.next_cursor || null);
    } catch (err) {
      if (current()) setMoreError(true);
    } finally {
      if (current()) {
        morePending.current = false;
        setMoreBusy(false);
      }
    }
  };

  const mutate = async (action) => {
    if (!mounted.current || mutating.current || morePending.current) return;
    mutating.current = true;
    ++loadVersion.current;
    setBusy(true);
    setActionError("");
    try {
      if (action === "read") {
        const r = await api.post("/notifications/read-all", undefined, { timeout: 30000 });
        if (r.data?.ok !== true) throw new Error("Read confirmation missing");
      } else {
        const r = await api.delete("/notifications", { timeout: 30000 });
        if (!Number.isInteger(r.data?.deleted) || r.data.deleted < 0) throw new Error("Clear confirmation missing");
      }
      if (!mounted.current) return;
      setItems((previous) => action === "read" ? previous.map((item) => ({ ...item, read: true })) : []);
      setUnread(0);
    } catch (err) {
      if (mounted.current) setActionError(action === "read" ? "Could not mark notifications as read. Please try again." : "Could not clear notifications. Please try again.");
    } finally {
      if (mounted.current) {
        mutating.current = false;
        setBusy(false);
        // Reconcile even an ambiguous failure; pre-action reads were invalidated.
        load();
      }
    }
  };

  const openPanel = () => {
    panelOpen.current = true;
    setOpen(true);
    if (unread > 0) mutate("read");
  };

  return (
    <>
      <button data-testid="notif-bell-btn" onClick={openPanel} className="relative brutal-btn p-2 bg-card" aria-label="Notifications">
        <Bell size={18} strokeWidth={2.5} />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-2 -right-2 bg-primary text-primary-foreground border-2 border-foreground rounded-full min-w-[20px] h-5 text-[10px] font-bold flex items-center justify-center px-1">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-40" data-testid="notif-panel" role="dialog" aria-modal="true" aria-labelledby="notifications-title">
          <button type="button" aria-label="Close notifications" className="absolute inset-0 bg-foreground/40" onClick={closePanel} />
          <div className="absolute z-10 right-4 top-20 w-[min(380px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto brutal-card bg-card p-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-1">
              <h3 id="notifications-title" className="text-xl font-display">Notifications</h3>
              <div className="flex gap-1">
                <button disabled={busy || moreBusy || loadState === "loading"} onClick={load} className="brutal-btn bg-card px-2 py-1 text-xs" data-testid="notif-refresh-btn">Refresh</button>
                {items.length > 0 && (
                  <button disabled={busy || moreBusy} onClick={() => mutate("clear")} className="brutal-btn bg-card px-2 py-1 text-xs" data-testid="notif-clear-all-btn">Clear all</button>
                )}
                <button aria-label="Close notifications" onClick={closePanel} className="brutal-btn bg-card p-1" data-testid="notif-close-btn"><X size={14} strokeWidth={2.5} /></button>
              </div>
            </div>
            {actionError && <p role="alert" className="p-2 text-sm">{actionError}</p>}
            {loadState === "error" && (
              <div role="alert" className="p-2 text-sm">
                Could not load notifications.
                <button disabled={busy} onClick={load} className="brutal-btn px-2 py-1 ml-2" data-testid="notif-retry-btn">Retry</button>
              </div>
            )}
            {items.length === 0 && loadState === "loading" && <p role="status" className="p-4 text-sm">Loading notifications…</p>}
            {items.length === 0 && loadState === "ready" ? (
              <div className="p-4 text-sm text-muted-foreground text-center">All quiet here. Nice and calm. 🌿</div>
            ) : (
              <div className="space-y-2">
                {items.map((n) => (
                  <NotificationItem key={n.id} n={n} onNavigate={closePanel} />
                ))}
              </div>
            )}
            {moreError && <p role="alert" className="p-2 text-sm">Could not load older notifications. Your current list is unchanged.</p>}
            {nextCursor && (
              <button disabled={busy || moreBusy || loadState !== "ready"} onClick={loadMore} className="brutal-btn bg-card w-full mt-3 p-2 text-sm" data-testid="notif-load-more-btn">
                {moreBusy ? "Loading older notifications…" : moreError ? "Retry older notifications" : "Load more"}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
